import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BriefcaseBusiness, Building2, Car, Hotel, PartyPopper, Search, ShoppingBag, Ticket, Wrench } from "lucide-react";
import Logo from "@/components/Logo";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";

const UNIVERSES = [
  { id: "immobilier", title: "Immobilier", short: "Biens, terrains, locaux", icon: Building2, accent: "#fe6c09", soft: "#fff1e7", setting: "property_enabled", defaultEnabled: true },
  { id: "automobile", title: "Automobile", short: "Voitures, motos, engins", icon: Car, accent: "#0866f5", soft: "#eaf3ff", setting: "auto_enabled", defaultEnabled: true },
  { id: "sejours", title: "Hôtels & séjours", short: "Hôtels et meublés", icon: Hotel, accent: "#5b32d6", soft: "#f0ecff", setting: "hospitality_enabled", defaultEnabled: true },
  { id: "espaces", title: "Espaces & événements", short: "Bureaux, salles, événements", icon: PartyPopper, accent: "#7c3aed", soft: "#f3eefe", setting: "spaces_enabled", defaultEnabled: true },
  { id: "billetterie", title: "Billetterie & inscriptions", short: "Billets, formations, événements", icon: Ticket, accent: "#ea580c", soft: "#fff2e8", setting: "ticketing_enabled", defaultEnabled: true, path: "/billetterie" },
  { id: "marketplace", title: "Marketplace", short: "Téléphones, mode, tech & marchandises", icon: ShoppingBag, accent: "#0f172a", soft: "#f1f5f9", setting: "marketplace_enabled", defaultEnabled: true, path: "/marketplace" },
  { id: "services", title: "Services", short: "Professionnels et prestataires", icon: Wrench, accent: "#087326", soft: "#eaf8ee", setting: "services_enabled", defaultEnabled: true },
  { id: "business", title: "Business & Projects", short: "Projets et opportunités", icon: BriefcaseBusiness, accent: "#4130b8", soft: "#efedff", setting: "business_enabled", defaultEnabled: false },
];

export default function Home() {
  const { settings } = useSiteSettings();
  const visibleUniverses = useMemo(() => {
    const visibility = settings?.portal_visibility || {};
    return UNIVERSES.filter((item) => {
      const configured = visibility[item.setting];
      const visible = configured == null ? item.defaultEnabled : configured !== false;
      return visible;
    });
  }, [settings?.portal_visibility]);

  return (
    <div className="home-portal-screen">
      <main className="home-portal-layout container-app pb-24 lg:pb-8">
        <section className="home-portal-intro" aria-labelledby="home-title">
          <div className="home-portal-logo" aria-label="Logo Team Work Africa">
            <Logo size="hero" framed />
          </div>
          <div className="home-portal-kicker">TEAM WORK AFRICA</div>
          <h1 id="home-title">Tout commence<br /><span>au même endroit.</span></h1>
          <p>Immobilier, automobile, séjours, événements, marketplace, billetterie, services et opportunités : un seul point d’entrée pour avancer.</p>
          <Link to="/demandes/nouvelle" className="home-portal-all-link">
            <span className="home-portal-all-icon"><Search className="h-4 w-4" /></span>
            <span><strong>Vous ne savez pas où commencer ?</strong><small>Décrivez simplement votre besoin</small></span>
            <ArrowRight className="h-5 w-5 shrink-0" />
          </Link>
        </section>

        <section className="home-portal-universes" aria-labelledby="universes-title">
          <div className="home-portal-heading">
            <div>
              <span className="home-portal-section-label">Choisir un univers</span>
              <h2 id="universes-title">Que voulez-vous faire aujourd’hui ?</h2>
              <p className="mt-1 text-sm text-slate-500">Choisissez l’univers correspondant à votre besoin.</p>
            </div>
            <span className="home-portal-count">{visibleUniverses.length} univers</span>
          </div>

          <div className="home-portal-grid">
            {visibleUniverses.map((universe) => {
              const Icon = universe.icon;
              return (
                <Link
                  key={universe.id}
                  to={universe.path || `/univers/${universe.id}`}
                  className="home-portal-card"
                  style={{ "--portal-accent": universe.accent, "--portal-soft": universe.soft }}
                  data-testid={`universe-launch-${universe.id}`}
                >
                  <span className="home-portal-card-icon"><Icon className="h-5 w-5" /></span>
                  <span className="home-portal-card-copy">
                    <strong>{universe.title}</strong>
                    <small>{universe.short}</small>
                  </span>
                  <ArrowRight className="home-portal-card-arrow h-4 w-4" />
                </Link>
              );
            })}
          </div>
        </section>
      </main>
      <div className="home-portal-footer-note">Un besoin. Un point d’entrée. Tout un écosystème.</div>
    </div>
  );
}
